import { useEffect, useState } from "react";
import { Routes, Route, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Building2, Home, FileText, FolderOpen, ClipboardList,
  Handshake, MessageSquare, Receipt, BarChart3, Settings as SettingsIcon,
  LogOut, Menu, X, ExternalLink,
} from "lucide-react";
import DashboardPage from "./pages/DashboardPage";
import CustomersPage from "./pages/CustomersPage";
import BusinessesPage from "./pages/BusinessesPage";
import PropertiesPage from "./pages/PropertiesPage";
import ApplicationsPage from "./pages/ApplicationsPage";
import ApplicationDetailPage from "./pages/ApplicationDetailPage";
import DocumentsPage from "./pages/DocumentsPage";
import TasksPage from "./pages/TasksPage";
import BrokersPage from "./pages/BrokersPage";
import MessagesPage from "./pages/MessagesPage";
import FeesPage from "./pages/FeesPage";
import ReportsPage from "./pages/ReportsPage";
import SettingsPage from "./pages/SettingsPage";

const NAV = [
  { id: "dashboard",     label: "Dashboard",             icon: LayoutDashboard, to: "/agency" },
  { id: "customers",     label: "Customers",             icon: Users,            to: "/agency/customers" },
  { id: "businesses",    label: "Businesses",            icon: Building2,        to: "/agency/businesses" },
  { id: "properties",    label: "Properties",            icon: Home,             to: "/agency/properties" },
  { id: "applications",  label: "Applications",          icon: FileText,         to: "/agency/applications" },
  { id: "documents",     label: "Documents",             icon: FolderOpen,       to: "/agency/documents" },
  { id: "tasks",         label: "Tasks & Follow-ups",    icon: ClipboardList,    to: "/agency/tasks" },
  { id: "brokers",       label: "Broker Partners",       icon: Handshake,        to: "/agency/brokers" },
  { id: "messages",      label: "Messages",              icon: MessageSquare,    to: "/agency/messages" },
  { id: "fees",          label: "Completions & Fees",    icon: Receipt,          to: "/agency/fees" },
  { id: "reports",       label: "Reports",               icon: BarChart3,        to: "/agency/reports" },
  { id: "settings",      label: "Settings",              icon: SettingsIcon,     to: "/agency/settings" },
];

const Sidebar = ({ me, onLogout, close }) => (
  <nav className="h-full flex flex-col bg-[#0F172A] text-slate-200">
    <div className="px-6 pt-6 pb-5 border-b border-white/10 flex items-start justify-between">
      <div>
        <img src="/logo.png" alt="Elin's Agency" className="h-10 w-auto" />
        <p className="text-[9px] tracking-[0.32em] uppercase text-amber-300 mt-2">Agency Workspace</p>
        <p className="text-[12px] text-slate-300 mt-1">{me?.name}</p>
      </div>
      {close && (
        <button onClick={close} className="lg:hidden text-slate-300 p-1" aria-label="Close menu"><X size={18} /></button>
      )}
    </div>
    <div className="flex-1 min-h-0 overflow-y-auto px-3 py-5 space-y-1">
      {NAV.map((item) => (
        <NavLink
          key={item.id}
          to={item.to}
          end={item.to === "/agency"}
          onClick={close}
          data-testid={`agency-nav-${item.id}`}
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-2.5 rounded-lg text-[13px] transition-colors ${
              isActive
                ? "bg-white/10 text-white font-semibold"
                : "text-slate-300 hover:bg-white/5 hover:text-white"
            }`
          }
        >
          <item.icon size={16} strokeWidth={1.7} />
          <span className="truncate">{item.label}</span>
        </NavLink>
      ))}
    </div>
    <div className="px-3 py-4 border-t border-white/10 space-y-1">
      <a href="/" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-[13px] text-slate-300 hover:bg-white/5 hover:text-white">
        <ExternalLink size={16} strokeWidth={1.7} /> View public site
      </a>
      <button onClick={onLogout} data-testid="agency-logout" className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-[13px] text-slate-300 hover:bg-white/5 hover:text-white">
        <LogOut size={16} strokeWidth={1.7} /> Sign out
      </button>
    </div>
  </nav>
);

export default function StaffWorkspace({ me, onLogout }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  return (
    <div className="min-h-screen bg-slate-50 text-slate-800" data-testid="staff-workspace">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 z-30">
        <Sidebar me={me} onLogout={onLogout} />
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden bg-[#0F172A] text-white sticky top-0 z-30">
        <div className="flex items-center justify-between px-4 py-3">
          <button onClick={() => setMobileOpen(true)} className="p-1" aria-label="Open menu"><Menu size={20} /></button>
          <img src="/logo.png" alt="Elin's Agency" className="h-7" />
          <button onClick={onLogout} className="p-1" aria-label="Sign out"><LogOut size={18} /></button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40">
          <button className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} aria-label="Close" />
          <aside className="absolute inset-y-0 left-0 w-72 max-w-[85vw]">
            <Sidebar me={me} onLogout={onLogout} close={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      <main className="lg:ml-64 p-5 sm:p-8 lg:p-10 max-w-[1400px]">
        <Routes>
          <Route index element={<DashboardPage />} />
          <Route path="customers" element={<CustomersPage />} />
          <Route path="customers/:id" element={<CustomersPage />} />
          <Route path="businesses" element={<BusinessesPage />} />
          <Route path="properties" element={<PropertiesPage />} />
          <Route path="applications" element={<ApplicationsPage />} />
          <Route path="applications/:id" element={<ApplicationDetailPage />} />
          <Route path="documents" element={<DocumentsPage />} />
          <Route path="tasks" element={<TasksPage />} />
          <Route path="brokers" element={<BrokersPage />} />
          <Route path="messages" element={<MessagesPage />} />
          <Route path="fees" element={<FeesPage />} />
          <Route path="reports" element={<ReportsPage />} />
          <Route path="settings" element={<SettingsPage />} />
        </Routes>
      </main>
    </div>
  );
}
